import { UserType } from "globals/types/models.ts";
import { useAppDispatch, useAppSelector } from "config/store";
import { adminActions } from "config/store/modules/admin.ts";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminConnectedContainer from "components/container/AdminConnectedContainer.tsx";
import Button from "components/atom/button/Button.tsx";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Input from "components/atom/Input.tsx";
import Icon from "components/atom/Icon.tsx";
import TabbedButtons from "components/layouts/TabbedButtons.tsx";
import { EnumUserTypeType } from "globals/types/globals.ts";

function AdminUsersPage() {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const users: UserType[] = useAppSelector(state => state.admin.users);

  const [type, setType] = useState<EnumUserTypeType | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  const searchUsers = useCallback(() => {
    dispatch(adminActions.getUsers(searchValue, type));
  }, [dispatch, searchValue, type]);

  //init by searching all events on mount
  //search event when it's empty
  useEffect(() => {
    if (searchValue === "") {
      searchUsers();
    }
  }, [searchUsers, searchValue]);

  const goTo = useCallback((userId: number) => {
    return () => {
      navigate(`/profile/admin/users/${userId}`);
    };
  }, [navigate]);

  return (
    <AdminConnectedContainer
      title={t("menu.admin.users")}
      rightTitle={(
        <Fragment>
          <Button small onClick={() => navigate("/profile/admin/users/create")}>
            {t("user.create")}
          </Button>
        </Fragment>
      )}>
      <div className="w-full space-y-2">
        <TabbedButtons
          onClick={(type) => {
            setType(type as EnumUserTypeType | null);
          }}
          selectedValue={type}
          buttons={[
            { label: t("forms.userTypes.all"), value: null },
            { label: t("forms.userTypes.viewer"), value: "viewer" },
            { label: t("forms.userTypes.event-manager"), value: "event-manager" }
          ]}
        />
        <div className="flex flex-row justify-center items-center gap-4 w-full"
             onKeyUp={(e) => {
               if (e.key === "Enter") {
                 searchUsers();
               }
             }}
        >
          <Input name="search" label="Recherche"
                 className="flex-1"
                 formikField={false}
                 value={searchValue}
                 onChange={(e) => {
                   const newValue = e.target.value;
                   setSearchValue(newValue);
                 }}
          />

          <button className="mt-5 w-12 h-10 bg-black rounded-md flex flex-row justify-center items-center"
                  type="button" onClick={searchUsers}>
            <Icon name="search" className="min-w-4 w-4 h-4 block text-white" />
          </button>
        </div>
        <div className="flex flex-row items-center justify-start gap-4 border-b border-gray-300">
          <div className="w-[40px]">
            <p className="text-sm font-medium text-black">
              ID
            </p>
          </div>
          <div className="w-3/12">
            <p className="text-sm font-medium text-black">
              {t("forms.name")}
            </p>
          </div>
          <div className="w-4/12">
            <p className="text-sm font-medium text-black">
              {t("forms.email")}
            </p>
          </div>
          <div className="w-2/12">
            <p className="text-sm font-medium text-black">
              {t("forms.planType")}
            </p>
          </div>
          <div className="w-2/12">
            <p className="text-sm font-medium text-black">
              {t("forms.trialEndedAtFuture")}
            </p>
          </div>
          <div className="w-1/12" />
        </div>

        {users.map(user => (
          <div
            className={`flex flex-row items-center justify-start gap-4 ${user.deletedAt ? "bg-red-100" : "bg-gray-50"}`}
            key={user.id}>
            <div className="pl-1 w-[40px]">
              <p className="text-black">
                #{user.id}
              </p>
            </div>
            <div className="w-3/12">
              <p className="text-black">
                {user.name}
              </p>
            </div>
            <div className="w-4/12">
              <p className="text-black">
                {user.email}
              </p>
            </div>
            <div className="w-2/12">
              <p className="text-black">
                {t(`plans.titles.${user.planType}`)}
              </p>
            </div>
            <div className="w-2/12">
              <p className="text-black">
                {user.trialEndedAt ? dayjs.utc(user.trialEndedAt).format("D MMM YYYY") : "-"}
              </p>
            </div>

            <div className="flex w-1/12 flex-row items-end justify-center">
              <button type="button" className="text-sm underline" onClick={goTo(user.id)}>
                {t("edit")}
              </button>
            </div>
          </div>
        ))}

      </div>
    </AdminConnectedContainer>
  );
}

export default AdminUsersPage;
